import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, ShieldAlert } from "lucide-react";
import { AppLayout } from "../../../components/layout";
import { ContentCard, LoadingState, ui } from "../../../components/ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { cn } from "../../../lib/utils";
import { getCanonicalRoles } from "../../../lib/auth";
import { expensePolicyService } from "../../../services/expensePolicyService";
import { conveyanceClaimService } from "../../../services/conveyanceClaimService";
import { defaultCurrency } from "../constants/claimConstants";
import {
  APPROVAL_FLOW,
  APPROVAL_FLOW_LABEL_KEY,
  CLAIM_TYPE,
  CLAIM_TYPE_LABEL_KEY,
  MAINTENANCE_FREQUENCY,
  MAINTENANCE_FREQUENCY_LABEL_KEY,
  MODE_LABEL_KEY,
  TRANSPORT_MODES,
  isHrRole,
  isOwnVehicleMode,
} from "../constants/conveyanceConstants";

/** Blank form state, used until a saved policy loads (or when none exists). */
function emptyPolicy() {
  return {
    policyId: null,
    claimType: CLAIM_TYPE.RATE_PER_KM,
    fixedMonthlyAmount: "",
    tollsParkingCovered: false,
    maintenanceSupported: false,
    maintenanceMaxBike: "",
    maintenanceMaxCar: "",
    maintenanceFrequency: MAINTENANCE_FREQUENCY.MONTHLY,
    submissionDeadlineDay: 7,
    expenseApprovalFlow: APPROVAL_FLOW.MANAGER,
    isActive: true,
  };
}

/**
 * All seven modes are always shown, so saved rows are overlaid onto a full
 * default set rather than rendering only what came back.
 */
function mergeModes(savedModes = []) {
  return TRANSPORT_MODES.map((modeTransport) => {
    const saved = savedModes.find((row) => row.modeTransport === modeTransport);
    return (
      saved ?? {
        modeId: null,
        modeTransport,
        isEnabled: false,
        ratePerKm: "",
        isAtActuals: !isOwnVehicleMode(modeTransport),
      }
    );
  });
}

const DEADLINE_DAYS = Array.from({ length: 31 }, (_, index) => index + 1);

const fieldLabel = "block text-xs font-semibold text-foreground mb-1.5";

const inputBase =
  "w-full h-10 px-3 rounded-lg border bg-white text-sm text-foreground focus:border-[var(--color-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/10";

const selectBase =
  "h-10 rounded-lg border bg-white text-sm w-full focus:ring-[var(--color-accent)]";

function borderFor(hasError) {
  return hasError ? "border-rose-500" : "border-[var(--color-text-border)]";
}

function FieldError({ message }) {
  if (!message) return null;
  return <p className="mt-1 text-[11px] font-semibold text-rose-500">{message}</p>;
}

/** Pill switch used for the two boolean policy flags. */
function Switch({ checked, onChange, disabled, label, id }) {
  return (
    <div className="flex items-center gap-2.5">
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          "relative h-6 w-11 shrink-0 rounded-full transition-colors cursor-pointer",
          "focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/30",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          checked ? "bg-[var(--color-primary)]" : "bg-gray-300"
        )}
      >
        <span
          className={cn(
            "absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform",
            checked && "translate-x-5"
          )}
        />
      </button>
      {label ? (
        <label htmlFor={id} className="text-sm font-semibold text-foreground cursor-pointer">
          {label}
        </label>
      ) : null}
    </div>
  );
}

function SectionHint({ children }) {
  return <p className="text-[11px] text-[var(--color-accent)] -mt-1 mb-3">{children}</p>;
}

/** "04 Aug 2026, 10:55" for the saved-confirmation banner. */
function formatSavedAt(value, t) {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return "";
  const monthKeys = [
    "dateTime.monthJan", "dateTime.monthFeb", "dateTime.monthMar", "dateTime.monthApr",
    "dateTime.monthMay", "dateTime.monthJun", "dateTime.monthJul", "dateTime.monthAug",
    "dateTime.monthSep", "dateTime.monthOct", "dateTime.monthNov", "dateTime.monthDec",
  ];
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(date.getDate())} ${t(monthKeys[date.getMonth()])} ${date.getFullYear()}, ${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
}

/**
 * Expense Policy — Local Conveyance (HRMS Team -> CoreHR Management).
 *
 * One policy row per tenant in org_expense_policy plus seven rows in
 * org_expense_policy_mode. Two independent axes drive visibility:
 *
 *   - Claim Type (Rate_Per_Km / Fixed_Monthly) governs whether the mode and
 *     rate list is shown OR the single fixed monthly amount. Switching only
 *     hides a section; the hidden section's values are kept in state and are
 *     nulled in the payload, so nothing the user typed is lost on screen.
 *   - Vehicle Maintenance support is always visible and independent of the
 *     Claim Type setting.
 *
 * Approval is a single dropdown because org_expense_policy stores one
 * `expense_approval_flow` column — the level count and each level's approver
 * are both implied by the chosen flow.
 *
 * Access is gated on the HR canonical role.
 */
export function ConveyancePolicyConfigPage() {
  const { t } = useTranslation();

  const isHR = useMemo(() => isHrRole(getCanonicalRoles()), []);

  const [policy, setPolicy] = useState(null);
  const [modes, setModes] = useState([]);
  const [baseline, setBaseline] = useState(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [errors, setErrors] = useState({});
  const [savedAt, setSavedAt] = useState("");
  // Currency label comes from the metrics master, not a literal — the policy
  // itself stores no currency, so this is display only.
  const [currency, setCurrency] = useState(null);

  const loadPolicy = useCallback(() => {
    setLoading(true);
    setLoadError("");
    expensePolicyService
      .fetchPolicy()
      .then(async (loaded) => {
        // No policy row yet -> first-time setup; Save will Add rather than Update.
        const nextPolicy = loaded ?? emptyPolicy();
        const nextModes = mergeModes(await expensePolicyService.fetchModes(loaded?.policyId));
        setPolicy(nextPolicy);
        setModes(nextModes);
        setBaseline(JSON.stringify({ policy: nextPolicy, modes: nextModes }));
      })
      .catch((error) => {
        console.error("Failed to load expense policy:", error);
        setLoadError(error.message || t("localConveyance.policyLoadError"));
      })
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => {
    if (isHR) loadPolicy();
    else setLoading(false);
  }, [isHR, loadPolicy]);

  useEffect(() => {
    if (!isHR) return;
    conveyanceClaimService
      .fetchCurrencies()
      .then((rows) => setCurrency(defaultCurrency(rows)))
      .catch((error) => console.warn("Currency metric lookup failed:", error));
  }, [isHR]);

  const isRatePerKm = policy?.claimType === CLAIM_TYPE.RATE_PER_KM;

  const isDirty = useMemo(
    () => Boolean(baseline) && JSON.stringify({ policy, modes }) !== baseline,
    [policy, modes, baseline]
  );

  const setField = (patch) => {
    setPolicy((prev) => ({ ...prev, ...patch }));
    setSavedAt("");
  };

  const setMode = (modeTransport, patch) => {
    setModes((prev) =>
      prev.map((mode) => (mode.modeTransport === modeTransport ? { ...mode, ...patch } : mode))
    );
    setSavedAt("");
  };

  const handleDiscard = () => {
    if (!baseline) return;
    const restored = JSON.parse(baseline);
    setPolicy(restored.policy);
    setModes(restored.modes);
    setErrors({});
    setSaveError("");
    setSavedAt("");
  };

  /**
   * Catches the empty required fields before a round trip. Value-range rules
   * belong to the backend and are surfaced from its error response.
   */
  const validate = () => {
    const next = {};
    const missing = (value) => value === "" || Number(value) <= 0;

    if (isRatePerKm) {
      const enabled = modes.filter((mode) => mode.isEnabled);
      if (enabled.length === 0) {
        next.modes = t("localConveyance.errorAtLeastOneMode");
      }
      enabled
        .filter((mode) => isOwnVehicleMode(mode.modeTransport))
        .forEach((mode) => {
          if (missing(mode.ratePerKm)) {
            next[`rate_${mode.modeTransport}`] = t("localConveyance.errorRateGreaterThanZero");
          }
        });
    } else if (missing(policy.fixedMonthlyAmount)) {
      next.fixedMonthlyAmount = t("localConveyance.errorAmountGreaterThanZero");
    }

    if (policy.maintenanceSupported) {
      if (missing(policy.maintenanceMaxBike)) {
        next.maintenanceMaxBike = t("localConveyance.errorAmountGreaterThanZero");
      }
      if (missing(policy.maintenanceMaxCar)) {
        next.maintenanceMaxCar = t("localConveyance.errorAmountGreaterThanZero");
      }
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = () => {
    setSaveError("");
    setSavedAt("");
    if (!validate()) return;

    setSaving(true);
    expensePolicyService
      .savePolicy({ policy, modes })
      .then(({ policy: savedPolicy, modes: savedModes }) => {
        const nextModes = mergeModes(savedModes);
        setPolicy(savedPolicy);
        setModes(nextModes);
        setBaseline(JSON.stringify({ policy: savedPolicy, modes: nextModes }));
        // The banner marks the moment this save completed, so it is stamped
        // client-side. The response's `modifiedOn` is not used: the update
        // endpoint echoes back the row's earlier stamp, which would freeze the
        // banner at the same time on every save.
        setSavedAt(new Date().toISOString());
      })
      .catch((error) => {
        console.error("Failed to save expense policy:", error);
        setSaveError(error.message || t("localConveyance.policySaveError"));
      })
      .finally(() => setSaving(false));
  };

  /* ------------------------------------------------------------- gating */

  if (!isHR) {
    return (
      <AppLayout>
        <ContentCard bodyClassName="p-10 text-center">
          <div className="mx-auto mb-3 h-12 w-12 rounded-2xl bg-[var(--color-workspace)]/60 grid place-items-center text-[var(--color-text-secondary)]">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <p className="text-sm font-bold text-foreground">
            {t("localConveyance.noAccessTitle")}
          </p>
          <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
            {t("localConveyance.noAccessMessage")}
          </p>
        </ContentCard>
      </AppLayout>
    );
  }

  return (
    <AppLayout className="space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-[26px] leading-tight font-extrabold tracking-tight text-foreground uppercase">
          {t("localConveyance.policyTitle")}
        </h1>
      </div>

      {savedAt ? (
        <div className="flex items-center gap-2.5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
          <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
          <p className="text-sm font-medium text-emerald-800">
            {t("localConveyance.policySavedAt", { savedAt: formatSavedAt(savedAt, t) })}
          </p>
        </div>
      ) : null}

      {loadError ? (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
          <p className="text-sm font-semibold text-rose-700">{loadError}</p>
          <button
            type="button"
            onClick={loadPolicy}
            className="h-8 shrink-0 rounded-lg border border-rose-300 bg-white px-3 text-[11px] font-bold text-rose-700 hover:bg-rose-100 transition cursor-pointer"
          >
            {t("localConveyance.retry")}
          </button>
        </div>
      ) : null}

      {saveError ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
          <p className="text-sm font-semibold text-rose-700">{saveError}</p>
        </div>
      ) : null}

      {loading || !policy ? (
        <ContentCard>
          <LoadingState message={t("localConveyance.loadingPolicy")} />
        </ContentCard>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
            {/* ------------------------------------ Claim type and rates */}
            <ContentCard
              title={t("localConveyance.sectionClaimType")}
              bodyClassName="p-4 sm:p-5"
            >
              <SectionHint>{t("localConveyance.claimTypeHint")}</SectionHint>

              <div className="mb-4 max-w-[260px]">
                <label className={fieldLabel}>{t("localConveyance.claimType")}</label>
                <Select
                  value={policy.claimType}
                  onValueChange={(value) => setField({ claimType: value })}
                >
                  <SelectTrigger className={cn(selectBase, "border-[var(--color-text-border)]")}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(CLAIM_TYPE).map((type) => (
                      <SelectItem key={type} value={type}>
                        {t(CLAIM_TYPE_LABEL_KEY[type])}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {isRatePerKm ? (
                <>
                  <p className={ui.sectionLabel}>{t("localConveyance.modeOfTransport")}</p>

                  <div className="mt-2 rounded-xl border border-[var(--color-text-border)] divide-y divide-[var(--color-text-border)]/60 overflow-hidden">
                    {TRANSPORT_MODES.map((modeTransport) => {
                      const mode =
                        modes.find((entry) => entry.modeTransport === modeTransport) || {};
                      const ownVehicle = isOwnVehicleMode(modeTransport);
                      const rateError = errors[`rate_${modeTransport}`];
                      return (
                        <div
                          key={modeTransport}
                          className="flex items-center justify-between gap-3 px-3 py-2.5"
                        >
                          <label className="flex items-center gap-2.5 cursor-pointer min-w-0">
                            <input
                              type="checkbox"
                              checked={Boolean(mode.isEnabled)}
                              onChange={(event) =>
                                setMode(modeTransport, { isEnabled: event.target.checked })
                              }
                              className="h-4 w-4 rounded border-[var(--color-text-border)] accent-[var(--color-primary)] cursor-pointer"
                            />
                            <span className="text-sm text-foreground truncate">
                              {t(MODE_LABEL_KEY[modeTransport])}
                            </span>
                          </label>

                          {/* Own Bike and Own Car carry SEPARATE rate_per_km values.
                              The five at-actuals modes never expose a rate field. */}
                          {ownVehicle ? (
                            <div className="w-[168px] shrink-0">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={mode.ratePerKm ?? ""}
                                disabled={!mode.isEnabled}
                                onChange={(event) =>
                                  setMode(modeTransport, { ratePerKm: event.target.value })
                                }
                                aria-label={t("localConveyance.ratePerKm")}
                                className={cn(
                                  inputBase,
                                  borderFor(rateError),
                                  "h-9 disabled:bg-gray-50 disabled:text-gray-400"
                                )}
                              />
                              <FieldError message={rateError} />
                            </div>
                          ) : (
                            <span className="shrink-0 text-xs text-[var(--color-text-secondary)]">
                              {t("localConveyance.atActuals")}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <FieldError message={errors.modes} />

                  <div className="mt-4">
                    <Switch
                      id="tolls-parking"
                      checked={Boolean(policy.tollsParkingCovered)}
                      onChange={(value) => setField({ tollsParkingCovered: value })}
                      label={t("localConveyance.tollsParkingCovered")}
                    />
                  </div>
                </>
              ) : (
                /* Fixed monthly amount — mutually exclusive with the mode list */
                <div className="max-w-[260px]">
                  <label className={fieldLabel}>
                    {t("localConveyance.fixedMonthlyAmount")}
                    {currency ? ` (${currency.abbreviation})` : ""}
                    <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={policy.fixedMonthlyAmount ?? ""}
                    onChange={(event) => setField({ fixedMonthlyAmount: event.target.value })}
                    className={cn(inputBase, borderFor(errors.fixedMonthlyAmount))}
                  />
                  <FieldError message={errors.fixedMonthlyAmount} />
                  <p className="mt-1 text-[11px] text-[var(--color-text-secondary)]">
                    {t("localConveyance.fixedMonthlyHint")}
                  </p>
                </div>
              )}
            </ContentCard>

            {/* ------------------- Vehicle maintenance + Timing and approval */}
            <div className="space-y-5">
              <ContentCard
                title={t("localConveyance.sectionMaintenance")}
                bodyClassName="p-4 sm:p-5"
              >
                <SectionHint>{t("localConveyance.maintenanceIndependentHint")}</SectionHint>

                <Switch
                  id="maintenance-supported"
                  checked={Boolean(policy.maintenanceSupported)}
                  onChange={(value) => setField({ maintenanceSupported: value })}
                  label={t("localConveyance.maintenanceSupport")}
                />

                {policy.maintenanceSupported ? (
                  <div className="mt-4 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className={fieldLabel}>
                          {t("localConveyance.maxMaintenanceBike")}
                          <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={policy.maintenanceMaxBike ?? ""}
                          onChange={(event) =>
                            setField({ maintenanceMaxBike: event.target.value })
                          }
                          className={cn(inputBase, borderFor(errors.maintenanceMaxBike))}
                        />
                        <FieldError message={errors.maintenanceMaxBike} />
                      </div>
                      <div>
                        <label className={fieldLabel}>
                          {t("localConveyance.maxMaintenanceCar")}
                          <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={policy.maintenanceMaxCar ?? ""}
                          onChange={(event) => setField({ maintenanceMaxCar: event.target.value })}
                          className={cn(inputBase, borderFor(errors.maintenanceMaxCar))}
                        />
                        <FieldError message={errors.maintenanceMaxCar} />
                      </div>
                    </div>

                    <div>
                      <label className={fieldLabel}>
                        {t("localConveyance.maintenanceFrequency")}
                      </label>
                      <Select
                        value={policy.maintenanceFrequency || undefined}
                        onValueChange={(value) => setField({ maintenanceFrequency: value })}
                      >
                        <SelectTrigger
                          className={cn(selectBase, borderFor(errors.maintenanceFrequency))}
                        >
                          <SelectValue placeholder={t("localConveyance.selectFrequency")} />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.values(MAINTENANCE_FREQUENCY).map((frequency) => (
                            <SelectItem key={frequency} value={frequency}>
                              {t(MAINTENANCE_FREQUENCY_LABEL_KEY[frequency])}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FieldError message={errors.maintenanceFrequency} />
                    </div>
                  </div>
                ) : null}
              </ContentCard>

              <ContentCard
                title={t("localConveyance.sectionTimingApproval")}
                bodyClassName="p-4 sm:p-5 space-y-4"
              >
                <div>
                  <label className={fieldLabel}>{t("localConveyance.deadlineDay")}</label>
                  <Select
                    value={String(policy.submissionDeadlineDay || "")}
                    onValueChange={(value) => setField({ submissionDeadlineDay: Number(value) })}
                  >
                    <SelectTrigger
                      className={cn(selectBase, borderFor(errors.submissionDeadlineDay))}
                    >
                      <SelectValue placeholder={t("localConveyance.selectDay")} />
                    </SelectTrigger>
                    <SelectContent className="max-h-60 overflow-y-auto">
                      {DEADLINE_DAYS.map((day) => (
                        <SelectItem key={day} value={String(day)}>
                          {day}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FieldError message={errors.submissionDeadlineDay} />
                </div>

                {/* Single dropdown — org_expense_policy stores one
                    expense_approval_flow column, so the level count and each
                    level's approver are both implied by the chosen flow. */}
                <div>
                  <label className={fieldLabel}>{t("localConveyance.approvalFlow")}</label>
                  <Select
                    value={policy.expenseApprovalFlow || undefined}
                    onValueChange={(value) => setField({ expenseApprovalFlow: value })}
                  >
                    <SelectTrigger
                      className={cn(selectBase, borderFor(errors.expenseApprovalFlow))}
                    >
                      <SelectValue placeholder={t("localConveyance.selectApprovalFlow")} />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(APPROVAL_FLOW).map((flow) => (
                        <SelectItem key={flow} value={flow}>
                          {t(APPROVAL_FLOW_LABEL_KEY[flow])}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FieldError message={errors.expenseApprovalFlow} />
                </div>
              </ContentCard>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-2xl border border-[var(--color-text-border)]/70 bg-white p-4 sm:p-5">
            <p className="text-[11px] text-[var(--color-text-secondary)] leading-relaxed max-w-[520px]">
              {t("localConveyance.snapshotNotice")}
            </p>
            <div className="flex items-center gap-3 shrink-0">
              <button
                type="button"
                onClick={handleDiscard}
                disabled={saving || !isDirty}
                className="h-11 px-5 rounded-xl border border-[var(--color-text-border)] text-sm font-semibold text-foreground hover:bg-gray-50 transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("localConveyance.discardChanges")}
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className={ui.primaryBtn}
              >
                {saving ? t("localConveyance.saving") : t("localConveyance.savePolicy")}
              </button>
            </div>
          </div>
        </>
      )}
    </AppLayout>
  );
}
